import React, { createContext, useContext, useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { findTabWarmHandler, normalizeTabWarmPath } from 'tabWarm/registry';
import { clearAllTabScopedDrafts, clearTabScopedDraftsForTab } from 'utils/tabDraftStorage';

// ─── Context ─────────────────────────────────────────────────────────────────
const TabStateContext = createContext(null);
const TabActionsContext = createContext(null);

const MAX_TABS = 15;

// ─── Constants ───────────────────────────────────────────────────────────────
const HOME_TAB_ID = 'tab-inicio';
export const TAB_SWITCH_NAVIGATION_SOURCE = 'tab-switch';
export const isTabSwitchNavigation = (location) =>
    location?.state?.navigationSource === TAB_SWITCH_NAVIGATION_SOURCE;
const INITIAL_HOME_TAB = {
    id: HOME_TAB_ID,
    title: 'Inicio',
    path: '/',
    icon: 'home',
    closable: false
};

const normalizeTabPath = (path) => {
    const normalized = `/${path || ''}`.replace(/\/+/, '/').replace(/\/+/g, '/');
    if (normalized === '/') return '/';
    return normalized.replace(/\/$/, '');
};

const INLINE_TAB_PARENT_PREFIXES = [
    { prefix: '/clients/catalogs', parent: '/clients/catalogs' },
    { prefix: '/clients/traffic', parent: '/clients/traffic' },
    { prefix: '/employees/employees', parent: '/employees/employees' },
    { prefix: '/employees/drivers', parent: '/employees/drivers' },
    { prefix: '/operators/traffic', parent: '/operators/traffic' },
    { prefix: '/units/catalogs', parent: '/units/catalogs' },
    { prefix: '/units/traffic', parent: '/units/traffic' },
    { prefix: '/trailers/catalogs', parent: '/trailers/catalogs' },
    { prefix: '/trailers/traffic', parent: '/trailers/traffic' },
    { prefix: '/client/client_group_passengers', parent: '/client/passengers' },
    { prefix: '/client/client_shift_passengers', parent: '/client/passengers' },
    { prefix: '/client/client_area_passengers', parent: '/client/passengers' },
    { prefix: '/client/client_supervisor_passengers', parent: '/client/passengers' },
    { prefix: '/vacation_request', parent: '/vacation_request_list' },
    { prefix: '/driver/vacation_request', parent: '/driver/vacation_request_list' },
    { prefix: '/extra_hour_request', parent: '/extra_hour_request' },
    { prefix: '/driver/extra_hour_request', parent: '/driver/extra_hour_request' },
];

const TAB_TITLE_OVERRIDES_KEY = 'tab_title_overrides_v1';

const readTabTitleOverrides = () => {
    if (typeof window === 'undefined') return {};
    try {
        const raw = window.sessionStorage.getItem(TAB_TITLE_OVERRIDES_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
        return {};
    }
};

const writeTabTitleOverrides = (value) => {
    if (typeof window === 'undefined') return;
    try {
        window.sessionStorage.setItem(TAB_TITLE_OVERRIDES_KEY, JSON.stringify(value || {}));
    } catch (error) {
    }
};

const resolveInlineTabPath = (path) => {
    const normalized = normalizeTabPath(path);
    const match = INLINE_TAB_PARENT_PREFIXES.find(({ prefix }) => (
        normalized === prefix || normalized.startsWith(`${prefix}/`)
    ));

    if (match) return match.parent;
    return normalized;
};

// Paths that should ALWAYS open in a new tab if not already present
export const isDeepLink = (path) => {
    const segments = path.split('/').filter(Boolean);
    return segments.length >= 3 || segments[segments.length - 1] === 'new';
};

// ─── ViewRegistrar ──────────────────────────────────────────────────────────
function ViewRegistrar({ registerActiveView, Component, routeProps, layoutConfig }) {
    useEffect(() => {
        if (Component && registerActiveView) {
            registerActiveView(Component, routeProps, layoutConfig);
        }
    }, [Component, registerActiveView, routeProps, layoutConfig]);
    return null;
}

// ─── Provider ─────────────────────────────────────────────────────────────────
function TabNavigationProvider({ children }) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const store = useStore();
    const modules = useSelector(state => state.app.user?.modules || []);
    const hasAuthenticatedSession = useSelector(state => (
        !!state.app.user
        || !!state.app.client
        || !!state.app.driver
        || !!state.app.passenger
        || !!state.app.supplier
        || !!state.app.superAdmin
    ));

    const [tabs, setTabs] = useState([INITIAL_HOME_TAB]);
    const [activeTabId, setActiveTabId] = useState(HOME_TAB_ID);
    const [tabWarmState, setTabWarmState] = useState({});

    const resolveMetadata = useCallback((path) => {
        const normalizedPath = (path || '').replace(/^\//, '').replace(/\/$/, '').toLowerCase();
        if (!normalizedPath) return { title: t('Inicio'), icon: 'home' };

        // 1. Precise match (highest priority)
        const mod = modules.find(m => {
            const mUrl = (m.url || '').replace(/^\//, '').replace(/\/$/, '').toLowerCase();
            return mUrl === normalizedPath;
        });
        if (mod) return { title: mod.name, icon: mod.icon || mod.menuIcon || mod.fa_icon };

        // 2. Parent match with weighting
        const baseSet = new Set(['dashboard', 'home', 'inicio']);
        const stripBase = (p) => {
            let parts = p.split('/');
            while (parts.length > 1 && baseSet.has(parts[0])) {
                parts = parts.slice(1);
            }
            return parts.join('/');
        };
        const strippedPath = stripBase(normalizedPath);

        const matches = modules.map(m => {
            const mUrl = (m.url || '').replace(/^\//, '').replace(/\/$/, '').toLowerCase();
            if (!mUrl) return null;

            let weight = 0;
            // Case A: Full path match
            if (normalizedPath.startsWith(mUrl + '/')) {
                weight = mUrl.length;
            }
            // Case B: Stripped path match (usually more specific)
            if (strippedPath.startsWith(mUrl + '/')) {
                // We add 1000 to prioritize matches that skip the "dashboard" container
                weight = Math.max(weight, mUrl.length + 1000);
            }
            
            // Penalty for generic bases to avoid them swallowing specific sub-routes
            if (baseSet.has(mUrl)) {
                weight = Math.min(weight, 10);
            }

            return weight > 0 ? { mod: m, weight } : null;
        }).filter(Boolean).sort((a, b) => b.weight - a.weight);

        const parentMod = matches[0]?.mod;
        if (parentMod) {
            const title = parentMod.name;
            return { title, icon: parentMod.icon || parentMod.menuIcon || parentMod.fa_icon };
        }

        return { title: null, icon: null };
    }, [modules, t]);

    const tabCacheRef = useRef({});
    const tabsRef = useRef([INITIAL_HOME_TAB]);
    const activeTabIdRef = useRef(HOME_TAB_ID);
    const titleOverridesRef = useRef(readTabTitleOverrides());
    const tabWarmStateRef = useRef({});
    const warmInFlightRef = useRef({});
    const warmScheduleRef = useRef({});
    const prevHasAuthenticatedSessionRef = useRef(hasAuthenticatedSession);

    const clearScheduledWarmJobs = useCallback(() => {
        Object.values(warmScheduleRef.current).forEach((job) => {
            if (!job) return;
            if (job.type === 'idle' && typeof window !== 'undefined' && typeof window.cancelIdleCallback === 'function') {
                window.cancelIdleCallback(job.id);
                return;
            }
            clearTimeout(job.id);
        });
        warmScheduleRef.current = {};
    }, []);

    useEffect(() => {
        tabsRef.current = tabs;
    }, [tabs]);

    useEffect(() => {
        if (tabWarmStateRef.current) {
            tabWarmStateRef.current = tabWarmState;
        }
    }, [tabWarmState]);

    // Re-resolve metadata when modules load (important for refresh on deep-linked routes)
    useEffect(() => {
        if (!modules || modules.length === 0) return;
        
        setTabs(prev => {
            let changed = false;
            const next = prev.map(tab => {
                if (tab.id === HOME_TAB_ID || tab.titleLocked) return tab;
                
                const metadata = resolveMetadata(tab.path);
                if (metadata.title && metadata.title !== tab.title) {
                    changed = true;
                    return { ...tab, title: metadata.title, icon: metadata.icon || tab.icon };
                }
                return tab;
            });
            if (changed) tabsRef.current = next;
            return changed ? next : prev;
        });
    }, [modules, resolveMetadata]);

    useEffect(() => {
        return () => {
            clearScheduledWarmJobs();
        };
    }, [clearScheduledWarmJobs]);

    const [activeView, setActiveView] = useState({
        Component: null,
        routeProps: {},
        layoutConfig: {}
    });

    const resolveHomeLandingPath = useCallback(() => {
        const appState = store.getState()?.app || {};
        const activeRole = sessionStorage.getItem('active_role');

        if (activeRole === 'supplier' && appState.supplier) return '/supplier/supplier_dashboard';
        if (activeRole === 'client' && appState.client) return '/client/client_dashboard';
        if (activeRole === 'driver' && appState.driver) return '/driver/driver_inspection_page';
        if (activeRole === 'passenger' && appState.passenger) return '/passenger/passengers/';

        if (appState.supplier) return '/supplier/supplier_dashboard';
        if (appState.client) return '/client/client_dashboard';
        if (appState.driver) return '/driver/driver_inspection_page';
        if (appState.passenger) return '/passenger/passengers/';

        return '/';
    }, [store]);

    const resetTabNavigationState = useCallback(({ pushHistory, clearPersistedTitles = false } = {}) => {
        clearScheduledWarmJobs();
        clearAllTabScopedDrafts();
        setTabs([INITIAL_HOME_TAB]);
        setActiveTabId(HOME_TAB_ID);
        setActiveView({
            Component: null,
            routeProps: {},
            layoutConfig: {}
        });
        setTabWarmState({});
        tabsRef.current = [INITIAL_HOME_TAB];
        activeTabIdRef.current = HOME_TAB_ID;
        tabCacheRef.current = {};
        tabWarmStateRef.current = {};
        warmInFlightRef.current = {};

        if (clearPersistedTitles) {
            titleOverridesRef.current = {};
            writeTabTitleOverrides({});
        }

        if (pushHistory) pushHistory(resolveHomeLandingPath());
    }, [clearScheduledWarmJobs, resolveHomeLandingPath]);

    useEffect(() => {
        const hadAuthenticatedSession = prevHasAuthenticatedSessionRef.current;
        if (hadAuthenticatedSession && !hasAuthenticatedSession) {
            resetTabNavigationState({ clearPersistedTitles: true });
        }
        prevHasAuthenticatedSessionRef.current = hasAuthenticatedSession;
    }, [hasAuthenticatedSession, resetTabNavigationState]);

    const isHomeEquivalentPath = useCallback((path) => {
        const normalizedPath = normalizeTabPath(path);
        const normalizedLandingPath = normalizeTabPath(resolveHomeLandingPath());

        if (normalizedPath === '/' || normalizedPath === normalizedLandingPath) return true;

        const knownLandingPaths = [
            '/supplier/supplier_dashboard',
            '/client/client_dashboard',
            '/driver/driver_inspection_page',
            '/passenger/passengers',
        ];

        return knownLandingPaths.some((candidate) => normalizeTabPath(candidate) === normalizedPath);
    }, [resolveHomeLandingPath]);

    const counterRef = useRef(0);
    const makeId = () => `tab-${++counterRef.current}`;

    const updateWarmState = useCallback((path, updater) => {
        const normalizedPath = normalizeTabWarmPath(path);
        setTabWarmState(prev => {
            const previous = prev[normalizedPath] || null;
            const nextEntry = typeof updater === 'function' ? updater(previous) : updater;
            const nextState = {
                ...prev,
                [normalizedPath]: nextEntry,
            };
            tabWarmStateRef.current = nextState;
            return nextState;
        });
    }, []);

    const getTabWarmState = useCallback((path) => {
        const normalizedPath = normalizeTabWarmPath(path);
        return tabWarmStateRef.current[normalizedPath] || null;
    }, []);

    const getPersistedTabTitle = useCallback((path) => {
        const normalizedPath = normalizeTabPath(path);
        return titleOverridesRef.current?.[normalizedPath] || null;
    }, []);

    const setPersistedTabTitle = useCallback((path, title) => {
        const normalizedPath = normalizeTabPath(path);
        if (!title || typeof title !== 'string') return;
        const next = {
            ...titleOverridesRef.current,
            [normalizedPath]: title,
        };
        titleOverridesRef.current = next;
        writeTabTitleOverrides(next);
    }, []);

    const executeWarmHandler = useCallback(async (path, handlerMatch) => {
        const normalizedPath = normalizeTabWarmPath(path);
        const { handler, params } = handlerMatch;

        delete warmScheduleRef.current[normalizedPath];
        warmInFlightRef.current[normalizedPath] = true;

        updateWarmState(normalizedPath, (previous) => ({
            ...previous,
            status: 'warming',
            error: null,
            lastStartedAt: Date.now(),
            handlerId: handler.id,
        }));

        try {
            const payload = await handler.prefetch({
                dispatch,
                getState: store.getState,
                path: normalizedPath,
                params,
            });

            updateWarmState(normalizedPath, (previous) => ({
                ...previous,
                status: 'ready',
                error: null,
                payload: payload ?? null,
                lastSuccessAt: Date.now(),
                handlerId: handler.id,
            }));
        } catch (error) {
            updateWarmState(normalizedPath, (previous) => ({
                ...previous,
                status: 'error',
                error: error?.message || 'No se pudo precargar la pestaña.',
                payload: previous?.payload ?? null,
                lastErrorAt: Date.now(),
                handlerId: handler.id,
            }));
        } finally {
            delete warmInFlightRef.current[normalizedPath];
        }
    }, [dispatch, store, updateWarmState]);

    const scheduleTabWarm = useCallback((path) => {
        const normalizedPath = normalizeTabWarmPath(path);
        const handlerMatch = findTabWarmHandler(normalizedPath);

        if (!handlerMatch) return null;

        const currentState = tabWarmStateRef.current[normalizedPath];
        const ttlMs = handlerMatch.handler.ttlMs ?? 45000;
        const isFresh = currentState?.status === 'ready'
            && currentState?.lastSuccessAt
            && (Date.now() - currentState.lastSuccessAt) < ttlMs;

        if (isFresh || warmInFlightRef.current[normalizedPath]) {
            return currentState;
        }

        if (warmScheduleRef.current[normalizedPath]) {
            const currentJob = warmScheduleRef.current[normalizedPath];
            if (currentJob.type === 'idle' && typeof window !== 'undefined' && typeof window.cancelIdleCallback === 'function') {
                window.cancelIdleCallback(currentJob.id);
            } else {
                clearTimeout(currentJob.id);
            }
        }

        const runWarm = () => executeWarmHandler(normalizedPath, handlerMatch);

        if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
            const id = window.requestIdleCallback(runWarm, { timeout: 1200 });
            warmScheduleRef.current[normalizedPath] = { type: 'idle', id };
        } else {
            const id = setTimeout(runWarm, 80);
            warmScheduleRef.current[normalizedPath] = { type: 'timeout', id };
        }

        updateWarmState(normalizedPath, (previous) => ({
            ...previous,
            status: 'queued',
            error: null,
            queuedAt: Date.now(),
            handlerId: handlerMatch.handler.id,
        }));

        return currentState;
    }, [executeWarmHandler, updateWarmState]);

    const openTab = useCallback((routeOrPath, pushHistory, preload = false) => {
        const path = typeof routeOrPath === 'string'
            ? `/${routeOrPath}`.replace(/\/+/g, '/')
            : `/${routeOrPath.path || routeOrPath.to || routeOrPath.url || ''}`.replace(/\/+/g, '/');

        const normalizedPath = normalizeTabPath(path);
        const tabPath = resolveInlineTabPath(normalizedPath);

        if (isHomeEquivalentPath(tabPath)) {
            activeTabIdRef.current = HOME_TAB_ID;
            setActiveTabId(HOME_TAB_ID);
            if (pushHistory) {
                pushHistory(normalizedPath === '/' ? resolveHomeLandingPath() : normalizedPath);
            }
            return;
        }

        const metadata = typeof routeOrPath === 'object' ? routeOrPath : resolveMetadata(tabPath);
        const explicitTitle = typeof routeOrPath === 'object'
            ? (routeOrPath.title || routeOrPath.name || null)
            : null;
        const persistedTitle = getPersistedTabTitle(tabPath);
        const title = explicitTitle
            || persistedTitle
            || metadata.title
            || metadata.name
            || tabPath.split('/').pop()
            || 'Cargando...';
        const titleLocked = !!(explicitTitle || persistedTitle);
        const icon = metadata.icon || metadata.menuIcon || metadata.fa_icon || null;
        const currentTabs = tabsRef.current;
        const existingTab = currentTabs.find(t => normalizeTabPath(t.path) === tabPath);
        const canWarm = preload && (!!existingTab || currentTabs.length < MAX_TABS);

        if (canWarm) {
            scheduleTabWarm(tabPath);
        }

        if (existingTab) {
            if (!preload) {
                activeTabIdRef.current = existingTab.id;
                setActiveTabId(existingTab.id);
            }
            if (pushHistory) pushHistory(normalizedPath);
            return;
        }

        if (currentTabs.length >= MAX_TABS) {
            if (pushHistory) pushHistory(normalizedPath);
            return;
        }

        const newTab = { id: makeId(), title, titleLocked, path: tabPath, icon, closable: true };
        const nextTabs = [...currentTabs, newTab];
        tabsRef.current = nextTabs;
        setTabs(nextTabs);
        if (titleLocked) {
            setPersistedTabTitle(tabPath, title);
        }
        if (!preload) {
            activeTabIdRef.current = newTab.id;
            setActiveTabId(newTab.id);
        }
        if (pushHistory) pushHistory(normalizedPath);
    }, [getPersistedTabTitle, isHomeEquivalentPath, resolveMetadata, scheduleTabWarm, setPersistedTabTitle, resolveHomeLandingPath]);

    const getTabIdForPath = useCallback((path) => {
        if (!path) return null;

        const normalizedCurrent = normalizeTabPath(path);
        const normalizedTabTarget = resolveInlineTabPath(normalizedCurrent);
        const matchedTab = tabs.find(tab => {
            const normalizedTab = normalizeTabPath(tab.path);
            return (
                normalizedTab === normalizedTabTarget
                || normalizedTab === normalizedCurrent
                || normalizedCurrent.startsWith(normalizedTab + '/')
                || (tab.id === HOME_TAB_ID && isHomeEquivalentPath(normalizedCurrent))
            );
        });

        return matchedTab?.id || null;
    }, [tabs, isHomeEquivalentPath]);

    const activateTab = useCallback((tabId, pushHistory) => {
        const tab = tabsRef.current.find(t => t.id === tabId);
        if (tab) {
            activeTabIdRef.current = tab.id;
            setActiveTabId(tab.id);
            if (pushHistory) {
                const path = tab.id === HOME_TAB_ID ? resolveHomeLandingPath() : tab.path;
                pushHistory(path);
            }
        }
    }, [resolveHomeLandingPath]);

    const registerActiveView = useCallback((Component, routeProps, layoutConfig = {}) => {
        const currentPath = routeProps?.location?.pathname;
        if (!currentPath) return;

        const normalizedCurrent = normalizeTabPath(currentPath);

        setActiveView(prev => {
            const previousPath = normalizeTabPath(prev.routeProps?.location?.pathname);
            if (prev.Component === Component && previousPath === normalizedCurrent) {
                return prev;
            }
            return { Component, routeProps, layoutConfig };
        });

        const currentTabs = tabsRef.current;
        const matchedTabId = getTabIdForPath(currentPath);
        const matchedTab = currentTabs.find(tab => tab.id === matchedTabId);

        if (matchedTab) {
            const cachedEntry = tabCacheRef.current[matchedTab.id];
            const cachedPath = normalizeTabPath(cachedEntry?.routeProps?.location?.pathname);
            const shouldUpdateCache = !cachedEntry
                || cachedEntry.Component !== Component
                || cachedPath !== normalizedCurrent;

            if (shouldUpdateCache) {
                const element = React.createElement(Component, { ...routeProps });
                tabCacheRef.current[matchedTab.id] = { Component, routeProps, layoutConfig, element };
            }
        } else if (isDeepLink(currentPath)) {
            openTab(currentPath, null, false);
        }
    }, [getTabIdForPath, openTab]);

    const closeTab = useCallback((tabId, pushHistory) => {
        if (tabId === HOME_TAB_ID) return;
        clearTabScopedDraftsForTab(tabId);
        setTabs(prev => {
            const idx = prev.findIndex(t => t.id === tabId);
            if (idx === -1) return prev;
            const next = prev.filter(t => t.id !== tabId);
            if (tabCacheRef.current[tabId]) delete tabCacheRef.current[tabId];
            setActiveTabId(current => {
                const updatedId = (() => {
                    if (current !== tabId) return current;
                    const newActive = next[Math.max(0, idx - 1)];
                    if (pushHistory) {
                        const nextPath = newActive.id === HOME_TAB_ID ? resolveHomeLandingPath() : newActive.path;
                        pushHistory(nextPath);
                    }
                    return newActive.id;
                })();
                activeTabIdRef.current = updatedId;
                return updatedId;
            });
            return next;
        });
    }, [resolveHomeLandingPath]);


    const closeAllTabs = useCallback((pushHistory) => {
        resetTabNavigationState({ pushHistory });
    }, [resetTabNavigationState]);

    const closeOtherTabs = useCallback((tabId, pushHistory) => {
        setTabs(prev => {
            const otherIds = prev.filter(t => t.id !== tabId && t.closable !== false).map(t => t.id);
            otherIds.forEach(id => clearTabScopedDraftsForTab(id));
            otherIds.forEach(id => { if (tabCacheRef.current[id]) delete tabCacheRef.current[id]; });
            const next = prev.filter(t => t.id === tabId || t.closable === false);
            setActiveTabId(tabId);
            activeTabIdRef.current = tabId;
            const activeTab = next.find(t => t.id === tabId);
            if (activeTab && pushHistory) {
                const nextPath = activeTab.id === HOME_TAB_ID ? resolveHomeLandingPath() : activeTab.path;
                pushHistory(nextPath);
            }
            return next;
        });
    }, [resolveHomeLandingPath]);

    const updateTabMetadata = useCallback((tabId, updates) => {
        setTabs(prev => {
            const next = prev.map(t => {
                if (t.id !== tabId) return t;
                const nextTab = { ...t, ...updates };
                if (typeof updates?.title === 'string' && updates.title.trim() !== '') {
                    nextTab.titleLocked = true;
                    setPersistedTabTitle(nextTab.path, updates.title);
                }
                return nextTab;
            });
            tabsRef.current = next;
            return next;
        });
    }, [setPersistedTabTitle]);

    const reorderTabs = useCallback((fromIndex, toIndex) => {
        setTabs(prev => {
            if (fromIndex === toIndex) return prev;
            // Don't allow moving the Home tab or moving anything before it
            if (fromIndex === 0 || toIndex === 0) return prev;
            if (fromIndex < 0 || fromIndex >= prev.length) return prev;
            if (toIndex < 0 || toIndex >= prev.length) return prev;

            const next = [...prev];
            const [moved] = next.splice(fromIndex, 1);
            next.splice(toIndex, 0, moved);
            return next;
        });
    }, []);

    const syncTabWithLocation = useCallback((pathname) => {
        const nextPath = `/${pathname || ''}`.replace(/\/+/g, '/');
        if (!nextPath || nextPath === '/login') return;

        const normalizedNext = normalizeTabPath(nextPath);
        const normalizedTabTarget = resolveInlineTabPath(normalizedNext);

        if (isHomeEquivalentPath(normalizedTabTarget)) {
            if (activeTabIdRef.current !== HOME_TAB_ID) {
                activeTabIdRef.current = HOME_TAB_ID;
                setActiveTabId(HOME_TAB_ID);
            }
            return;
        }

        // Use the ref to check existing tabs without triggering a render-loop via setTabs
        const existingTab = tabsRef.current.find(t => {
            const normalizedTab = normalizeTabPath(t.path)
            return normalizedTab === normalizedNext || normalizedNext.startsWith(normalizedTab + '/')
                || normalizedTab === normalizedTabTarget
        })

        if (existingTab) {
            if (existingTab.id !== activeTabIdRef.current) {
                activeTabIdRef.current = existingTab.id;
                setActiveTabId(existingTab.id);
            }
            return;
        }

        if (isDeepLink(nextPath)) {
            openTab(nextPath, null, true);
            return;
        }

        const currentTabId = activeTabIdRef.current;
        if (!currentTabId || currentTabId === HOME_TAB_ID) {
            openTab(nextPath, null, false);
            return;
        }

        const activeIndex = tabsRef.current.findIndex(t => t.id === currentTabId);
        if (activeIndex === -1) return;

        setTabs(prevTabs => {
            const nextTabs = [...prevTabs];
            if (tabCacheRef.current[currentTabId]) delete tabCacheRef.current[currentTabId];
            nextTabs[activeIndex] = { ...nextTabs[activeIndex], path: nextPath };
            return nextTabs;
        });
    }, [isHomeEquivalentPath, openTab]);

    const stateValue = useMemo(() => ({
        tabs,
        activeTabId,
        activeView,
        tabWarmState,
        tabCache: tabCacheRef.current,
        isAtMaxTabs: tabs.length >= MAX_TABS,
        maxTabs: MAX_TABS
    }), [tabs, activeTabId, activeView, tabWarmState]);

    const actionsValue = useMemo(() => ({
        openTab,
        getTabIdForPath,
        activateTab,
        closeTab,
        closeAllTabs,
        closeOtherTabs,
        registerActiveView,
        updateTabMetadata,
        reorderTabs,
        syncTabWithLocation,
        scheduleTabWarm,
        getTabWarmState,
    }), [openTab, getTabIdForPath, activateTab, closeTab, closeAllTabs, closeOtherTabs, registerActiveView, updateTabMetadata, reorderTabs, syncTabWithLocation, scheduleTabWarm, getTabWarmState]);

    return (
        <TabStateContext.Provider value={stateValue}>
            <TabActionsContext.Provider value={actionsValue}>
                {children}
            </TabActionsContext.Provider>
        </TabStateContext.Provider>
    );
}

function useTabNavigation() {
    const state = useContext(TabStateContext);
    const actions = useContext(TabActionsContext);
    if (!state || !actions) {
        return {
            openTab: () => {},
            activeTabId: null,
            updateTabMetadata: () => {},
            getTabIdForPath: () => null,
            activateTab: () => {},
            closeTab: () => {},
            closeAllTabs: () => {},
            closeOtherTabs: () => {},
            registerActiveView: () => {},
            reorderTabs: () => {},
            syncTabWithLocation: () => {},
            scheduleTabWarm: () => {},
            getTabWarmState: () => ({}),
            tabs: [],
            activeView: null,
            tabWarmState: {},
            tabCache: {},
            isAtMaxTabs: false,
            maxTabs: 10,
        };
    }
    return { ...state, ...actions };
}

export { TabNavigationProvider, ViewRegistrar, useTabNavigation };
